function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useCallback, useMemo } from 'react';
import { List } from '../ActionList/List';
import { DropdownButton } from './DropdownButton';
import { AnchoredOverlay } from '../AnchoredOverlay';
import { useProvidedRefOrCreate } from '../hooks/useProvidedRefOrCreate';
import { useProvidedStateOrCreate } from '../hooks/useProvidedStateOrCreate';

/**
 * A `DropdownMenu` provides an anchor (button by default) that will open a floating menu of selectable items.  The menu can be
 * opened and navigated using keyboard or mouse.  When an item is selected, the menu will close and the `onChange` callback will be called.
 * If the default anchor button is used, the anchor contents will be updated with the selection.
 */
export function DropdownMenu({
  renderAnchor = props => /*#__PURE__*/React.createElement(DropdownButton, props),
  anchorRef: externalAnchorRef,
  placeholder,
  selectedItem,
  onChange,
  overlayProps,
  items,
  open,
  onOpenChange,
  ...listProps
}) {
  const [combinedOpenState, setCombinedOpenState] = useProvidedStateOrCreate(open, onOpenChange, false);
  const onOpen = useCallback(() => setCombinedOpenState(true), [setCombinedOpenState]);
  const onClose = useCallback(() => setCombinedOpenState(false), [setCombinedOpenState]);
  const anchorRef = useProvidedRefOrCreate(externalAnchorRef);
  const renderMenuAnchor = useMemo(() => {
    if (renderAnchor === null) {
      return null;
    }

    return props => {
      var _selectedItem$text;

      return renderAnchor({ ...props,
        children: (_selectedItem$text = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.text) !== null && _selectedItem$text !== void 0 ? _selectedItem$text : placeholder
      });
    };
  }, [placeholder, renderAnchor, selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.text]);
  const itemsToRender = useMemo(() => {
    return items.map(item => {
      return { ...item,
        role: 'option',
        selected: item === selectedItem,
        onAction: (itemFromAction, event) => {
          var _item$onAction;

          (_item$onAction = item.onAction) === null || _item$onAction === void 0 ? void 0 : _item$onAction.call(item, itemFromAction, event);

          if (event.defaultPrevented) {
            return;
          }

          onClose();
          onChange === null || onChange === void 0 ? void 0 : onChange(item === selectedItem ? undefined : item);
        }
      };
    });
  }, [items, onChange, onClose, selectedItem]);
  return /*#__PURE__*/React.createElement(AnchoredOverlay, {
    renderAnchor: renderMenuAnchor,
    anchorRef: anchorRef,
    open: combinedOpenState,
    onOpen: onOpen,
    onClose: onClose,
    overlayProps: overlayProps
  }, /*#__PURE__*/React.createElement(List, _extends({}, listProps, {
    role: "listbox",
    items: itemsToRender
  })));
}
DropdownMenu.displayName = "DropdownMenu";
DropdownMenu.displayName = 'DropdownMenu';